import React, { useMemo } from "react";
import * as THREE from "three";
import { Text } from "@react-three/drei";

const GridResults3D = ({ grid, results = [], gridScale = 1, showValues = true }) => {
    const cells = useMemo(() => {
        if (!grid?.origin || !Array.isArray(results)) return [];

        const { origin, xSpacing = 1, ySpacing = 1, edgeStart = false } = grid;

        const width = origin.x.length;
        const height = origin.y.length;

        const actualCols = Math.floor(width / xSpacing);
        const actualRows = Math.floor(height / ySpacing);

        const cols = edgeStart ? actualCols + 1 : actualCols;
        const rows = edgeStart ? actualRows + 1 : actualRows;

        const adjustedCellWidth = edgeStart
            ? (width * gridScale) / (cols - 1)
            : (width * gridScale) / cols;

        const adjustedCellHeight = edgeStart
            ? (height * gridScale) / (rows - 1)
            : (height * gridScale) / rows;

        const startX = origin.x.start * gridScale;
        const startY = origin.y.start * gridScale * -1;
        const z = 0.11;

        const scaleX = grid.scaleX ?? 1;
        const scaleY = grid.scaleY ?? 1;
        const rotationDeg = grid.angle ?? 0;
        const rotationRad = -(rotationDeg * Math.PI) / 180;

        const anchor = new THREE.Vector2(startX, startY);

        const items = [];

        for (let row = 0; row < rows; row++) {
            for (let col = 0; col < cols; col++) {
                const idx = row * cols + col;
                const value = results[idx] ?? null;

                const offsetX = edgeStart ? 0 : adjustedCellWidth / 2;
                const offsetY = edgeStart ? 0 : adjustedCellHeight / 2;

                const rawX = startX + col * adjustedCellWidth + offsetX;
                const rawY = startY - row * adjustedCellHeight - offsetY;

                const p = new THREE.Vector2(rawX, rawY).sub(anchor);
                p.x *= scaleX;
                p.y *= scaleY;

                const rotated = new THREE.Vector2(
                    p.x * Math.cos(rotationRad) - p.y * Math.sin(rotationRad),
                    p.x * Math.sin(rotationRad) + p.y * Math.cos(rotationRad)
                ).add(anchor);

                items.push({
                    position: [rotated.x, rotated.y, z],
                    value,
                });
            }
        }

        return items;
    }, [grid, results, gridScale]);

    return (
        <>
            {grid?.showResults &&
                (() => {
                    const maxValue = Math.max(...cells.map(cell => cell.value));
                    const xSpacing = grid.xSpacing
                    const ySpacing = grid.ySpacing

                    return cells.map((cell, i) => {
                        const isMax = Math.abs(cell.value - maxValue) < 0.0001;
                        const color = isMax ? "green" : cell.value >= 1 ? "cyan" : "red";
                        const opacity = cell.value != null ? 0.6 : 0;

                        return (
                            <group key={i} position={cell.position}>
                                {/* Render Cells (if showValues is false) */}
                                {!showValues && (
                                    <mesh>
                                        <planeGeometry args={[1 / xSpacing, 1 / ySpacing]} />
                                        <meshBasicMaterial color={color} transparent opacity={opacity} />
                                    </mesh>
                                )}
                                
                                {/* Render Text (if showValues is true) */}
                                {showValues && cell.value != null && (
                                    <Text
                                        position={[0, 0, 0.1]} // Slight offset for the text
                                        fontSize={0.4 * gridScale}
                                        color={color}
                                        anchorX="center"
                                        anchorY="middle"
                                    >
                                        {Number(cell.value).toFixed(2)} {/* Convert to number before calling toFixed */}
                                    </Text>
                                )}
                            </group>
                        );
                    });
                })()}
        </>
    );
};

export default GridResults3D;
